import React, { useEffect, useRef, useState } from "react";
import { IMAGES } from "@/data/content";
import { cn } from "@/lib/utils";

// Smart image: fades in elegantly on load, decodes async, lazy-loads by default
// (or eager + high fetch priority for above-the-fold/LCP images), and swaps to a
// verified fallback if the source fails – so a broken image is never shown.
export function ImageWithFallback({
  src,
  fallback = IMAGES.product1,
  alt = "",
  className,
  loading = "lazy",
  priority = false,
  draggable,
  onLoad,
}) {
  const [current, setCurrent] = useState(src);
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef(null);

  // Reset when the source changes (e.g. lightbox reuse).
  useEffect(() => {
    setCurrent(src);
    setLoaded(false);
  }, [src]);

  // Set fetchpriority via the DOM to stay compatible across React versions.
  useEffect(() => {
    if (priority && imgRef.current) {
      try {
        imgRef.current.setAttribute("fetchpriority", "high");
      } catch (e) {
        /* noop */
      }
    }
  }, [priority]);

  // If the (cached) image is already complete on mount, mark loaded.
  useEffect(() => {
    if (imgRef.current && imgRef.current.complete && imgRef.current.naturalWidth > 0) {
      setLoaded(true);
    }
  }, []);

  return (
    <img
      ref={imgRef}
      src={current}
      alt={alt}
      loading={priority ? "eager" : loading}
      decoding="async"
      draggable={draggable}
      onLoad={(e) => {
        setLoaded(true);
        if (onLoad) onLoad(e);
      }}
      onError={() => {
        if (current !== fallback) setCurrent(fallback);
        else setLoaded(true);
      }}
      className={cn(
        className,
        "transition-opacity duration-700 ease-out",
        loaded ? "opacity-100" : "opacity-0"
      )}
    />
  );
}
